import React, { useEffect } from "react"

import styled from "styled-components"

import { useLocalization } from "@izettle/lingo-client"
import { colors, borderRadius, spacing, IconCircleTickSmall, IllustrationHouseSvg } from "@izettle/otto"
import { Text, Icon, Illustration } from "@izettle/react"

import { Cancel } from "@/components/cancel"
import { FileSelector } from "@/components/file-selector"
import { PageWrapper } from "@/components/layout-helpers"
import { useSetPageTitle } from "@/components/page-title"
import { baseUrl } from "@/constants"
import { useThemeInfo } from "@/hooks/use-theme-info"
import { SelectFilesProps } from "@/models"
import { theme } from "@/theme"

const ImageContainer = styled.div`
  height: 200px;
  max-width: 197px;
  margin: ${spacing.large} ${spacing.none} ${spacing.small};
`;

const PageTitle = styled(Text.Title2)`
  margin: ${spacing.none} ${spacing.none} ${spacing.small};
`

const List = styled.ul`
  list-style-type: none;
  display: flex;
  flex-direction: column;
  background-color: ${colors.message.background.info};
  border-radius: ${borderRadius.medium};
  padding: ${spacing.small};
  margin-bottom: ${spacing.xLarge};
`

const ListItem = styled.li``

const SubList = styled.ul`
  list-style-type: none;
  margin: ${spacing.none};
  padding: ${spacing.none};
`

const FirstListItemHeader = styled(Text.RegularBold)<{ paypalTheme : boolean}>`
  margin: ${spacing.none} ${spacing.none} ${spacing.small};
  color: ${({paypalTheme}) => paypalTheme ? colors.text.default : colors.text.info }; 
`

const ListItemHeader = styled(FirstListItemHeader)<{ paypalTheme : boolean}>`
  margin: ${spacing.small} ${spacing.none} ${spacing.small};
  color: ${({paypalTheme}) => paypalTheme ? colors.text.default : colors.text.info }; 
`

const SubListItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: ${spacing.small};

  &:last-child {
    margin-bottom: ${spacing.none};
  }
`

const ItemText = styled(Text.Regular)<{ paypalTheme : boolean}>`
  margin: ${spacing.none};
  color: ${({paypalTheme}) => paypalTheme ? colors.text.default : colors.text.info }; 
`

const ItemIcon = styled(Icon)<{ paypalTheme : boolean}>`
  margin-right: ${spacing.xxSmall};
  color: ${({paypalTheme}) => paypalTheme ? colors.text.default : colors.text.info }; 

  @media ${theme.devices.tinyMobile} {
    display: none;
  }

  @media ${theme.devices.mobile} {
    display: inline;
  }
`

const examples: string[] = [
  "PROOF_OF_ADDRESS_EXAMPLE_LIST_UTILITY_BILL",
]

const poaRequirements: string[] = [
  "PROOF_OF_ADDRESS_REQUIREMENTS_LIST_OFFICIALLY_ISSUED",
  "PROOF_OF_ADDRESS_EXAMPLE_LIST_ADDRESS",
  "PROOF_OF_ADDRESS_EXAMPLE_LIST_NAME",
  "PROOF_OF_ADDRESS_EXAMPLE_LIST_DATE",
]

export type OverviewProps = {
  selectFiles: SelectFilesProps
}

export const Overview: React.FC<OverviewProps> = ({ selectFiles }) => {
  const { text } = useLocalization()
  const { setPageTitle } = useSetPageTitle()
  const { paypalTheme } = useThemeInfo()

  useEffect(() => {
    setPageTitle(text("PROOF_OF_ADDRESS"))
  }, [])

  return (
    <PageWrapper>
      <Cancel url={baseUrl} />
      <ImageContainer>
      <Illustration illustration={IllustrationHouseSvg} alt="" width="100%" height="200px" aria-hidden  />
      </ImageContainer>
      <PageTitle id="proof_of_address_title">{text("PROOF_OF_ADDRESS")}</PageTitle>
      <List role="list" aria-labelledby="proof_of_address_title">
        <ListItem role="listitem">
          <FirstListItemHeader id="poa_title_1" paypalTheme={paypalTheme}>
            {text("PROOF_OF_ADDRESS_EXAMPLE_TITLE")}
          </FirstListItemHeader>
          <SubList aria-labelledby="poa_title_1">
            {examples.map((example) => (
              <SubListItem key={example}>
                <ItemIcon svgIcon={IconCircleTickSmall} paypalTheme={paypalTheme}/>
                <ItemText paypalTheme={paypalTheme}>{text(example)} </ItemText>
              </SubListItem>
            ))}
          </SubList>
        </ListItem>
        <ListItem role="listitem">
          <ListItemHeader id="poa_title_2" paypalTheme={paypalTheme}>
            {text("PROOF_OF_ADDRESS_EXAMPLE_TITLE_TWO")}
          </ListItemHeader>
          <SubList aria-labelledby="poa_title_2">
            {poaRequirements.map((poaRequirement) => (
              <SubListItem key={poaRequirement}>
                <ItemIcon svgIcon={IconCircleTickSmall} paypalTheme={paypalTheme}/>
                <ItemText paypalTheme={paypalTheme}>{text(poaRequirement)}</ItemText>
              </SubListItem>
            ))}
          </SubList>
        </ListItem>
      </List>
      <FileSelector
        name="proofOfAddress"
        type="generic"
        selectFiles={selectFiles}
        redirect={`${baseUrl}/proofOfAddress/upload`}
      >
        {text("PURPOSE_UPLOAD_BUTTON")}
      </FileSelector>
    </PageWrapper>
  )
}
